import type { User } from '@/api/auth';
import type { CMSBonus } from '@/api/cms';
import { currencyNameMap } from '@/helpers/currency';
import { unmaskAmount } from '@/helpers/validators';
import type { ICMSPromocode } from '@/modules/common/hooks/useCMSPromocodes';

export const getCurrentUserBonus = <T extends { code: string | null }>(
  bonuses: T[],
  user: User
) => {
  const bonus = bonuses.find(({ code }) => {
    if (!code) return false;
    return (
      !!user.depositBonusCode &&
      code
        .split(',')
        .map((c) => c.trim())
        .includes(user.depositBonusCode)
    );
  });
  return bonus || null;
};

export const getMinDepositAmount = (
  minDepArray: { currency: string; amount: number }[] | undefined,
  userCurrency: string
) => {
  if (!minDepArray) return 0;
  const normalized = [userCurrency, currencyNameMap[userCurrency]]
    .filter((c): c is string => c !== undefined)
    .map((c) => c.toLowerCase());
  const minDep = minDepArray.find(({ currency }) =>
    normalized.includes(currency.toLowerCase())
  );
  return minDep?.amount || 0;
};

export const getMinBonusDepByBonus = (bonuses: CMSBonus[], user: User) => {
  if (!user.depositBonusCode) return 0;
  const bonus = getCurrentUserBonus(bonuses, user);
  return getMinDepositAmount(bonus?.minDepV2, user.currency);
};

export const getMinBonusDepByPromocode = (
  promocodes: ICMSPromocode[],
  user: User
) => {
  if (!user.depositBonusCode) return 0;
  const bonus = getCurrentUserBonus(promocodes, user);
  return getMinDepositAmount(bonus?.minDep, user.currency);
};

export const getBonusSpinValue = (
  bonuses: ICMSPromocode[] | CMSBonus[],
  user: User
) => {
  if (!user.depositBonusCode) return 0;
  const bonus = getCurrentUserBonus(bonuses as CMSBonus[], user);
  return getMinDepositAmount(bonus?.spinValue, user.currency);
};

export const isBonusActivated = (
  code: string | null,
  depositBonusCode?: string
) => code === depositBonusCode;

export const isDepositLocked = (
  currentAmount: string | number,
  minDep?: number
) => Number(minDep) > Number(unmaskAmount(currentAmount));
